import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

const cinemmerseProImages = [
  {
    src: '/01-WebsiteFiles/CinemmersePro/CinemmersePro-About.webp',
    alt: 'Cinemmerse Pro About Section',
    title: 'About Section',
    description: 'Overview of the Cinemmerse Pro interface and theatrical delivery capabilities'
  },
  {
    src: '/01-WebsiteFiles/CinemmersePro/CinemmersePro-Convert.webp',
    alt: 'Cinemmerse Pro Conversion Process',
    title: 'Conversion Process',
    description: 'Real-time conversion of IMF IAB masters to DCI-compliant DCP IAB assets'
  },
  {
    src: '/01-WebsiteFiles/CinemmersePro/CinemmersePro-ConvertDone.webp',
    alt: 'Cinemmerse Pro Conversion Complete',
    title: 'Conversion Complete',
    description: 'Successfully converted DCP IAB assets ready for theatrical distribution'
  }
];

const CinemmerseProSlider: React.FC = () => {
  const [isClient, setIsClient] = React.useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const openPreview = (index: number) => {
    setSelectedImageIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closePreview = () => {
    setSelectedImageIndex(null);
    document.body.style.overflow = 'unset';
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    if (selectedImageIndex === null) return;
    
    if (direction === 'prev') {
      setSelectedImageIndex(selectedImageIndex === 0 ? cinemmerseProImages.length - 1 : selectedImageIndex - 1);
    } else {
      setSelectedImageIndex(selectedImageIndex === cinemmerseProImages.length - 1 ? 0 : selectedImageIndex + 1);
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedImageIndex !== null) {
        closePreview();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [selectedImageIndex]);

  if (!isClient) {
    return <div className="h-96 bg-gradient-to-br from-zinc-900 to-black rounded-3xl border border-white/5 animate-pulse"></div>;
  }

  return (
    <section className="relative w-full bg-gradient-to-b from-zinc-900/50 via-zinc-950/30 to-zinc-900/50 py-12 overflow-hidden rounded-3xl border border-zinc-800/50">
      {/* Ambient glows matching website theme */}
      <div className="absolute -top-20 left-1/2 w-[32rem] h-[32rem] -translate-x-1/2 rounded-full blur-[120px] opacity-40 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)' }} />
      <div className="absolute bottom-0 right-0 w-[24rem] h-[24rem] translate-x-1/4 translate-y-1/4 rounded-full blur-[120px] opacity-35 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)' }} />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(15,23,42,0.25),transparent_55%)] pointer-events-none" />
      <div className="absolute inset-0 mix-blend-soft-light opacity-15 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)', backgroundSize: '4px 4px' }} />

      <div className="relative w-full">
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <p className="uppercase tracking-[0.6em] text-xs text-blue-200/70 mb-4">Interface Preview</p>
            <h2 className="text-3xl sm:text-4xl font-semibold text-white">Cinemmerse Pro in Action</h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">Experience the professional theatrical delivery workflow with real-time conversion monitoring and SMPTE compliance validation.</p>
            <p className="text-blue-300/60 text-sm mt-2 animate-pulse">💡 Click any image to view in full-screen</p>
          </div>

          <div className="relative rounded-[2.5rem] border border-white/5 bg-gradient-to-br from-white/5 via-white/0 to-white/5 backdrop-blur-xl shadow-[0_20px_80px_rgba(8,47,73,0.25)] overflow-hidden">
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-black/10 to-black/40" />
            <div className="swiper-container w-full">
              <Swiper
                spaceBetween={30}
                centeredSlides={true}
                loop={true}
                autoplay={{
                  delay: 4000,
                  disableOnInteraction: false,
                }}
                pagination={{
                  clickable: true,
                }}
                navigation={true}
                modules={[Autoplay, Pagination, Navigation]}
                style={{
                  '--swiper-navigation-color': '#3b82f6',
                  '--swiper-pagination-color': '#3b82f6',
                } as React.CSSProperties}
                className="mySwiper"
                breakpoints={{
                  640: {
                    slidesPerView: 1,
                  },
                  768: {
                    slidesPerView: 1,
                  },
                  1024: {
                    slidesPerView: 1,
                  },
                }}
              >
                {cinemmerseProImages.map((image, index) => (
                  <SwiperSlide key={index} className="p-6">
                    <div className="relative h-full rounded-3xl border border-white/10 bg-black/40 backdrop-blur group overflow-hidden">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.08),transparent_55%)] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                      <div className="relative w-full flex flex-col items-center justify-center px-4 py-8">
                        <div className="relative w-full flex justify-center">
                          <div className="absolute inset-0 rounded-2xl blur-3xl opacity-20 scale-110 bg-blue-500/30 group-hover:opacity-50 transition" />
                          <img
                            src={image.src}
                            alt={image.alt}
                            className="relative w-full h-80 md:h-96 lg:h-[28rem] xl:h-[32rem] object-contain drop-shadow-[0_25px_40px_rgba(8,47,73,0.4)] rounded-xl cursor-pointer hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                            decoding="async"
                            width={1200}
                            height={800}
                            onClick={() => openPreview(index)}
                            style={{ contentVisibility: 'auto' }}
                          />
                        </div>
                        <div className="mt-8 text-center text-white max-w-2xl">
                          <h3 className="text-2xl font-semibold mb-3">{image.title}</h3>
                          <p className="text-gray-300 text-base leading-relaxed">{image.description}</p>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </div>

      {/* Full-screen preview modal */}
      {selectedImageIndex !== null && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
          onClick={closePreview}
        >
          <div className="relative w-full h-full flex items-center justify-center p-4">
            {/* Close button */}
            <button
              onClick={closePreview}
              className="modal-button absolute top-8 right-8 z-50 p-2 rounded-full bg-black/40 text-white backdrop-blur-sm transition-all duration-300 hover:bg-black/60 hover:scale-110 hover:shadow-lg hover:shadow-sky-500/20 relative overflow-hidden"
              aria-label="Close preview"
            >
              <svg className="w-6 h-6 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-500 ease-out rounded-full"></div>
            </button>

            {/* Previous button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateImage('prev');
              }}
              className="modal-button absolute left-4 sm:left-6 md:left-8 lg:left-12 top-1/2 -translate-y-1/2 z-50 p-2 sm:p-3 rounded-full bg-black/40 text-white backdrop-blur-sm transition-all duration-300 hover:bg-black/60 hover:scale-110 hover:shadow-lg hover:shadow-sky-500/20 relative overflow-hidden"
              aria-label="Previous image"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-500 ease-out rounded-full"></div>
            </button>

            {/* Next button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateImage('next');
              }}
              className="modal-button absolute right-4 sm:right-6 md:right-8 lg:right-12 top-1/2 -translate-y-1/2 z-50 p-2 sm:p-3 rounded-full bg-black/40 text-white backdrop-blur-sm transition-all duration-300 hover:bg-black/60 hover:scale-110 hover:shadow-lg hover:shadow-sky-500/20 relative overflow-hidden"
              aria-label="Next image"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-500 ease-out rounded-full"></div>
            </button>

            {/* Image container */}
            <div 
              className="relative w-full h-full flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={cinemmerseProImages[selectedImageIndex].src}
                alt={cinemmerseProImages[selectedImageIndex].alt}
                className="max-w-[95vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
                loading="eager"
                decoding="async"
              />
              
              {/* Image info */}
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-center text-white bg-black/60 backdrop-blur-md rounded-lg px-6 py-3 max-w-md">
                <h3 className="text-lg font-semibold mb-1">{cinemmerseProImages[selectedImageIndex].title}</h3>
                <p className="text-sm text-gray-300">{cinemmerseProImages[selectedImageIndex].description}</p>
              </div>
            </div>

            {/* Image counter */}
            <div className="absolute bottom-4 right-4 text-white bg-black/50 backdrop-blur-sm rounded-lg px-3 py-1 text-sm">
              {selectedImageIndex + 1} / {cinemmerseProImages.length}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CinemmerseProSlider;
